import { Component, OnInit, Input, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { OrchestrationService } from '../services/orchestration-service/orchestration.service';
import { Observable, Subscription } from 'rxjs';
import { ApiResponseState } from '../../store/api-response/api-response-state.model';
import { SnackBarService } from "../../common/services/snack-bar/snack-bar.service";
import { ApiResponseActions } from '../../store/api-response/api-response-actions';
import { select } from '@angular-redux/store';
import { ProvisioningMapping } from './mapping/provisioning-mapping';
import { SelectedVehicleState } from '../../store/selected-vehicle-store/selected-vehicle-state.model';
import { TranslationService } from '../../common/services/languages/translation.service';
import { transformDateTime } from '../../common/helpers/utils';
import { AdminDispatcher } from '../../store/admin-store/admin-dispatcher';
import { SidenavService } from '../services/sidenav.service';
import { ApiResponseDispatcher } from '../../store/api-response/api-response-dispatcher';
import { NgIf, NgFor } from '@angular/common';
import { MatCard, MatCardTitle, MatCardContent } from '@angular/material/card';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'app-provisioning',
    templateUrl: './provisioning.component.html',
    styleUrls: ['./provisioning.component.scss'],
    imports: [NgIf, MatCard, MatCardTitle, MatButton, MatIcon, MatCardContent, NgFor, TranslatePipe]
})
export class ProvisioningComponent implements OnInit, OnChanges, OnDestroy {
  /**
   * @property Will contain vehicle details payload
   */
  @Input() vehicleDetails: object;
  /**
   * @property provisioning will contain provisioning details
   */
  public provisioningValues: object;
  /**
   * @property used to store the api subscription
   */
  private apiSubscriber: Subscription;
  /**
   * @property used to store the api subscription
   */
  private selectedVehicleSubscriber: Subscription;
  /**
  * Property to observe the redux customer store
   **/
  @select('api') readonly apiObservable: Observable<ApiResponseState>;
  /**
   * @property provisioningMapping to store the values and names for provisioning
   */
  public provisioningMapping: Array<object>;
  /**
  *@arg selectedVehicleObservable to observe selectedVehicle observable
   **/
  @select('selectedVehicle') readonly selectedVehicleObservable: Observable<SelectedVehicleState>;
  /**
  *@arg globalProvisioning store if the global provisioning is true or not
   **/
  public globalProvisioning: boolean;

  /**
   * @property dcmFlagsObject will contian the DCM Flags from the provisioning values
   */
  public dcmFlagsObject: any;

  /**
   * @property dcmFlagsObject will contian the Head Unit Flags from the provisioning values
   */
  public huFlagsObject: any;


  /**
   * @property plugNCharge will contain the PNC Flags from the provisioning values
   */
  public pncStatusData: any;
  /**
   * @property isSuperUser Indicates whether the current user has the SUPER_USER role. This flag is set by checking if the user has the 'modifyDcm' permission,
   * which corresponds to having the 'SUPER_USER' role.
   * @type {boolean}
   */
  @Input() isSuperUser: boolean;
  /**
   * Indicates whether the current user has admin privileges.This flag is set by checking if the user has the 'retryProvision' permission,
   * which corresponds to having either the 'TSTAdmin' base role or the 'OP_ADMIN' sub-role.
   * @type {boolean}
   */
  @Input() isAdmin: boolean;
  /**
   * @property lastRequestedVin stores the last VIN used to call provisioning APIs to avoid duplicate calls
   */
  private lastRequestedVin: string = '';

  constructor(
    private snackBarService: SnackBarService,
    private orch: OrchestrationService,
    private translate: TranslationService,
    private adminDispatcher: AdminDispatcher,
    private sidenav: SidenavService,
    public apiDispatcher: ApiResponseDispatcher
  ) { }
  /**
   * @function loadProvisioningForVehicle triggers provisioning/PnC requests whenever VIN changes
   * @param vehicle selected vehicle object
   */
  private loadProvisioningForVehicle(vehicle: any): void {
    const vin = vehicle && vehicle['vin'];
    if (!vin) {
      return;
    }
    if (this.lastRequestedVin === vin) {
      return;
    }
    this.lastRequestedVin = vin;
    this.vehicleDetails = vehicle;
    this.provisioningValues = null;
    this.dcmFlagsObject = {};
    this.huFlagsObject = {};
    this.pncStatusData = null;
    this.orch.getProvisioningRequest(vin);
    const fuelType = vehicle['fuelType'];
    if (fuelType === 'Electric' || fuelType === 'Plug-in Hybrid') {
      this.orch.getPlugAndChargeStatus(vehicle['brand'], vin, vehicle['region']);
    }
  }

  /**
   * @function ngOnInit Method to Initializes the component and perform initial operations
   */
  ngOnInit() {
    this.provisioningMapping = ProvisioningMapping;
    this.loadProvisioningForVehicle(this.vehicleDetails);
    this.selectedVehicleSubscriber = this.selectedVehicleObservable.subscribe({
      next: ({ vehicle }) => {
        if (vehicle) {
          this.loadProvisioningForVehicle(vehicle);
        }
      },
      complete: () => this.selectedVehicleSubscriber && this.selectedVehicleSubscriber.unsubscribe()
    }
    );
    this.apiSubscriber = this.apiObservable.subscribe((api) => {
      const temp = api.apiAction === ApiResponseActions.SET_PROVISION_HISTORY && api.apiResponse['type'] === 'success' && !!api.apiResponse['payload'] ? 
        this.provisionHistorySuccess() : api.apiResponse['type'] === 'error' ? this.provisionHistoryError(api) : null;

      if (api.apiAction === ApiResponseActions.SET_GET_PROVISIONING) {
        if (api.getProvisioning['type'] === 'success' && !!api.getProvisioning['payload']) {
          this.provisioningValues = api.getProvisioning['payload'];
          this.dcmFlagsObject = this.provisioningValues && this.provisioningValues['dcm'] ? this.provisioningValues['dcm']['flags']: null;
          this.huFlagsObject = this.provisioningValues && this.provisioningValues['headunit'] ? this.provisioningValues['headunit']['flags']: null;
          this.globalProvisioning = this.provisioningValues ? this.provisioningValues['globalProvisioning'] : null;
          this.pncStatusData = this.provisioningValues ? this.provisioningValues['pnc'] : null;
          this.provisioningMapping = ProvisioningMapping.map((item) => this.formatProvisioningItem(item, this.provisioningValues)
          );
        } if (this.vehicleDetails && this.vehicleDetails['fuelType'] !== 'Electric' && this.vehicleDetails['fuelType'] !== 'Plug-in Hybrid') {
          if (!this.provisioningValues) {
            return;
          }
          this.pncStatusData = this.provisioningValues['pnc'];
          this.pncStatusData = { toggle_status : false };
          this.provisioningValues['pnc'] = this.pncStatusData;
          this.provisioningValues['pnc']['status'] = "N/A";
          this.provisioningMapping = ProvisioningMapping.map((item) => this.formatProvisioningItem(item, this.provisioningValues));
        } else {
          return;
        }
      }
      if (api.apiAction === ApiResponseActions.SET_PNC_STATUS) {
        if (!!api.apiResponse['payload'] && api.apiResponse['type'] === 'success' && !!api.getProvisioning['payload']) {
          if (!this.provisioningValues) {
            this.provisioningValues = api.getProvisioning['payload'];
          }
          this.pncStatusData = api.apiResponse['payload'];
          this.provisioningValues['pnc'] = this.pncStatusData;
          const certDetails =
            this.provisioningValues &&
            this.provisioningValues['pnc'] &&
            this.provisioningValues['pnc']['cert_details']
              ? this.provisioningValues['pnc']['cert_details']
              : null;

          if (Array.isArray(certDetails) && certDetails.length >= 2) {
            this.provisioningValues['pnc']['lastUpdateDate'] = certDetails[1].start_date;
            this.provisioningValues['pnc']['toy_cert'] = certDetails[0].active;
            this.provisioningValues['pnc']['prov_cert'] = certDetails[1].active;
            this.provisioningValues['pnc']['root_cert'] = certDetails[1].active;
            this.provisioningValues['pnc']['prov_status'] = certDetails[1].progress;
            this.provisioningValues['pnc']['contract_status'] = certDetails[0].progress;
          }
          this.provisioningValues['pnc']['status'] = 'received';
          this.provisioningMapping = ProvisioningMapping.map((item) => this.formatProvisioningItem(item, this.provisioningValues));
        } else if (!api.apiResponse['payload'] && api.apiResponse['type'] === 'error') {
          if (!this.provisioningValues) {
            this.provisioningValues = {};
          }
          this.pncStatusData = { toggle_status : false };
          this.provisioningValues['pnc'] = this.pncStatusData;
          this.provisioningValues['pnc']['status'] = "FAILED";
          this.provisioningMapping = ProvisioningMapping.map((item) => this.formatProvisioningItem(item, this.provisioningValues));
        }
      }
      if (api.apiAction === ApiResponseActions.SET_RETRIGER_PROVISIONING) {
        if (api.retrigerProvisioning['type'] === 'success') {
          this.orch.getProvisioningRequest(this.vehicleDetails['vin']);
        } else {
          this.snackBarService.showToast('Re-Trigger Provisioning data failed to load.');
        }
      }
    });
  }
  /**
   * @function ngOnChanges triggers provisioning/PnC requests when parent updates the input vehicleDetails
   * @param changes SimpleChanges object from Angular
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.vehicleDetails && changes.vehicleDetails.currentValue) {
      this.loadProvisioningForVehicle(changes.vehicleDetails.currentValue);
    }
  }
  /**
   * @function ngOnDestroy Method to finalize the component
   */
  ngOnDestroy() {
    if (this.apiSubscriber) {
      this.apiSubscriber.unsubscribe();
    }
    if (this.selectedVehicleSubscriber) {
      this.selectedVehicleSubscriber.unsubscribe();
    }
  }
  /**
   * @function getSubproperty function to show nested properties
   */
  public getSubproperty(pathList, obj): string {
    const propertyMap = pathList.map(path => {
      const properties = Array.isArray(path) ? path : path.split('.');
      const propertyReducer = properties.reduce((prev, curr) => prev && prev[curr], obj);
      return propertyReducer;
    });
    return propertyMap.find(property => property !== undefined);
  }
  /**
   * @function getParsedDateTime retrieves the parsed date time for the provisioning status
   */
  public getParsedDateTime(datePath: string): string {
    const date = this.getSubproperty(datePath, this.provisioningValues);
    if (date) {
      return transformDateTime(date);
    }
    return;
  }

  /**
   * @method formatProvisioningValue formats the provisioning value to handle boolean
   */
  private formatProvisioningValue(provisioningValue: any): string {
    if (typeof provisioningValue === 'string') {
      return provisioningValue.toLowerCase();
    } else if (typeof provisioningValue === 'boolean') {
      return provisioningValue ? 'on' : 'off';
    }
    return;
  }

  /**
   * @function getProvisioningValue used to get the nested value
   */
  public getProvisioningValue(value: string) {
    const rawProvisionValue = !!value[0] ? this.getSubproperty(value, this.provisioningValues) : '';
    const provisionValue = this.formatProvisioningValue(rawProvisionValue);
    const formattedValue = provisionValue === 'on' ? this.translate.getCustomTranslation('provisioning.labels.on')['value'].toUpperCase() :
      provisionValue === 'full' ? this.translate.getCustomTranslation('provisioning.labels.on')['value'].toUpperCase() :
      provisionValue === 'on' ? this.translate.getCustomTranslation('provisioning.labels.on')['value'].toUpperCase() :
      provisionValue === 'off' ? this.translate.getCustomTranslation('provisioning.labels.off')['value'].toUpperCase() :
      provisionValue === 'none' ? this.translate.getCustomTranslation('provisioning.labels.off')['value'].toUpperCase() :
      provisionValue === 'ack' ? this.translate.getCustomTranslation('provisioning.labels.pending')['value'].toUpperCase() :
      provisionValue ? provisionValue.toUpperCase() : '';
    return formattedValue;
  }
  /**
   * @function formatProvisioningItem gets the values of the item formatted
   */
  private formatProvisioningItem(item, itemList) {
    const name = item['name'];
    const type = item['type'];
    const parent = item['parent'];
    const status = this.getSubproperty(item.status, itemList);
    const failureReason = status === 'FAILED' ? this.getSubproperty(item.message, itemList) : 'N/A';
    const value = this.getProvisioningValue(item.value);
    const date = this.getParsedDateTime(item.updatedDate);
    const formattedStatus = status && status.toString() === '100' ? 'INSTALLED' : status !== undefined && status.toString() === '0' ? 'CANCELED' : status && status.toLowerCase() === 'ack' ? 'PENDING' :
      status && status.toLowerCase() === 'received' ? 'SUCCESS' : status ;
    return {
      name,
      type,
      parent,
      status: formattedStatus,
      failureReason,
      value,
      date
    };
  }

  /**
   * @function retrigerProvisioning retrigers provisioning
   */
  public retriggerProvisioning(type: string) {
    const vin = this.vehicleDetails['vin'];
    const guid = this.vehicleDetails['subscriberGuid'];
    const brand = this.vehicleDetails['brand'];
    this.orch.retrigerProvisioning(vin, guid, brand, (type !== 'package') ? (!this.globalProvisioning ? 'package' : type) : type);
  }
  /**
   * @function cannotRetriggerService can retriger or not
   */
  public cannotRetriggerService(status) {
    return !(this.isAdmin && (status === 'FAILED' || status === 'PENDING')
      && (!!this.vehicleDetails['subscriptionStatus'] && (this.vehicleDetails['subscriptionStatus'] === 'SUBSCRIBED' || this.vehicleDetails['subscriptionStatus'] === 'ACTIVE')));
  }
  /**
   * @function canRetriggerVehicle cannot retrigger vehicle or not
   */
  public canRetriggerVehicle(): boolean {
    return this.isAdmin;
  }
  /**
   * @function refreshStatus refresh provisioning status
   */
  public refreshStatus(): void {
    if (this.vehicleDetails) {
      this.orch.getProvisioningRequest(this.vehicleDetails['vin']);
    }
    if (this.vehicleDetails && (this.vehicleDetails['fuelType'] === 'Electric' || this.vehicleDetails['fuelType'] === 'Plug-in Hybrid') ) {
      this.orch.getPlugAndChargeStatus(this.vehicleDetails['brand'], this.vehicleDetails['vin'], this.vehicleDetails['region']);
    }
  }
  /**
   * @function getProvisionHistory gets the subscription history of the account
* @param vin Vin number of the vehicle
   */
  public getProvisionHistory(): void {
    this.orch.getProvisionHistory(this.vehicleDetails['vin'], 0, 10);
  }
  /**
   * @method provisionHistorySuccess will enable the provision history side nav
   **/
  public provisionHistorySuccess(): void {
    this.adminDispatcher.setUpdateInfo('provision-history', 'provision-history', 'provision-history', '');
    this.sidenav.open();
    window.scrollTo(0, 0);
  }
  /**
   * @function provisionHistory Action after an error from API response
* @arg provisionHistoryError object
* @returns void
   **/
  public provisionHistoryError(error: object): void {
    const message = error && error['apiAction'] === ApiResponseActions.SET_PNC_STATUS
    ? "Plug and Charge Provisioning data failed to load."
    : "Provisioning data failed to load.";
    this.apiDispatcher.setProvisionHistoryResponse({});
    this.snackBarService.showToast(message);
  }

  /**
   * @function hasModifyDcmPerm Check for user role generation to view re-trigger button
* @returns boolean
   **/
  public hasModifyDcmPerm(): boolean {
    return this.isSuperUser && (this.vehicleDetails['generation'] === '21MM' || this.vehicleDetails['generation'] === '17CYPLUS');
  }


  /**
   * @function activateDCM will activate the DCM
 * @returns void
   **/
  public activateDCM(): void {
    this.orch.addDCMActivation(this.vehicleDetails);
  }

  /**
   * @function toggleAppFeaturesSidenav toggles the side nav to open the show all flags side nav
   **/
  public toggleSidenavComponent(componentName: string): void {
    this.adminDispatcher.setUpdateInfo(componentName, '', '', '', { dcm: this.dcmFlagsObject, headunit: this.huFlagsObject });
    this.sidenav.toggle();
    window.scrollTo(0, 0);
  }

}
